import {
  type Anchor,
  type NonZeroVector,
  type Point,
  point,
  quadraticBezierCurve,
  vector,
} from '../elements';
import {
  EDGE_ARROW_HEAD_HEIGHT,
  EDGE_HIT_AREA_THICKNESS,
  EDGE_TIP_ELEMENT_DISTANCE_FROM_NODE,
} from '../graphConstants';
import { nodeTypeIcons } from '../nodes/icons/icons';
import type { NodeData } from '../nodes/types';
import {
  PI_RADIANS_90,
  type Radians,
  convertRadiansToIntegerDegrees,
  faceRight,
  isFacingRight,
} from '../utils/angles';
import {
  EDGE_CIRCLE_RADIUS,
  type EdgePositions,
  type EdgePositionsCircle,
  type EdgePositionsLine,
} from './EdgePositions';
import { type EdgeDataForPositions, getArrowHeadPoints } from './positions';

/**
 * Distance of middle point on loopback edge from node icon's center
 */
const LOOPBACK_EDGE_MID_DISTANCE = 150;
const LOOPBACK_EDGE_ANGLE = PI_RADIANS_90;
const LOOPBACK_EDGE_MID_LABEL_DISTANCE = 1;
const LOOPBACK_EDGE_TIP_LABEL_DISTANCE = 40;
const LOOPBACK_EDGE_CONTROL_POINT_DISTANCE =
  LOOPBACK_EDGE_MID_DISTANCE / Math.cos(LOOPBACK_EDGE_ANGLE / 2);

function ensureLabelWithAnchorFaceRight(
  angle: Radians,
  anchor: Anchor,
): { anchor: Anchor; angle: Radians } {
  const up = faceRight(angle);
  return {
    anchor: isFacingRight(angle)
      ? anchor
      : ({ x: 1 - anchor.x, y: 1 - anchor.y } as Anchor),
    angle: convertRadiansToIntegerDegrees(up),
  };
}

export const getLoopbackEdgeEndAngle = (shift: number): number =>
  shift + LOOPBACK_EDGE_ANGLE;

const placeEdgeElements = (
  edgeDataForPositions: EdgeDataForPositions,
  startPoint: Point,
  startVector: NonZeroVector,
  endPoint: Point,
  endVector: NonZeroVector,
) => {
  let startCurve = startPoint;
  let endCurve = endPoint;
  let circle: EdgePositionsCircle | undefined;
  const arrowHeads: EdgePositions['arrowHeads'] = {};
  if (edgeDataForPositions.direction !== 'undirected') {
    const endArrowTip = point.createOnVector(
      endPoint,
      endVector,
      EDGE_TIP_ELEMENT_DISTANCE_FROM_NODE,
    );
    arrowHeads.target = getArrowHeadPoints(
      endArrowTip,
      endVector,
      EDGE_HIT_AREA_THICKNESS,
      EDGE_ARROW_HEAD_HEIGHT,
    );
    endCurve = point.createOnVector(
      endCurve,
      endVector,
      EDGE_ARROW_HEAD_HEIGHT + EDGE_TIP_ELEMENT_DISTANCE_FROM_NODE,
    );
    if (edgeDataForPositions.direction === 'bidirected') {
      const startArrowTip = point.createOnVector(
        startPoint,
        startVector,
        EDGE_TIP_ELEMENT_DISTANCE_FROM_NODE,
      );
      arrowHeads.source = getArrowHeadPoints(
        startArrowTip,
        startVector,
        EDGE_HIT_AREA_THICKNESS,
        EDGE_ARROW_HEAD_HEIGHT,
      );
      startCurve = point.createOnVector(
        startCurve,
        startVector,
        EDGE_ARROW_HEAD_HEIGHT + EDGE_TIP_ELEMENT_DISTANCE_FROM_NODE,
      );
    }
  }
  if (edgeDataForPositions.circle) {
    // Start the edge at the center of the circle - this way the edge line will be
    // nicely attached to the circle without gaps and without overlapping it on the other end.
    startCurve = point.createOnVector(
      startPoint,
      startVector,
      EDGE_CIRCLE_RADIUS + EDGE_TIP_ELEMENT_DISTANCE_FROM_NODE,
    );
    circle = {
      radius: EDGE_CIRCLE_RADIUS,
      position: startCurve,
    };
  }
  return {
    arrowHeads,
    circle,
    startCurve,
    endCurve,
  };
};

export function getLoopbackEdgePositions(
  nodeData: NodeData,
  edgeDataForPositions: EdgeDataForPositions,
): EdgePositions {
  if (
    edgeDataForPositions.direction === 'bidirected' &&
    edgeDataForPositions.circle
  ) {
    throw new Error('Rendering circle on bidirected edge is not supported');
  }

  const nodeTypeIcon = nodeTypeIcons[nodeData.type];

  const startAngle = edgeDataForPositions.shift;
  const endAngle = getLoopbackEdgeEndAngle(edgeDataForPositions.shift);
  const midAngle = startAngle + (endAngle - startAngle) / 2;

  const startVector = vector.unit(startAngle);
  const endVector = vector.unit(endAngle);

  const getClippingPoint = nodeTypeIcon.makeGetClippingPoint(nodeData.style);

  const startPoint = point.createOnVector(
    nodeData.position,
    getClippingPoint(startVector),
  );
  const endPoint = point.createOnVector(
    nodeData.position,
    getClippingPoint(endVector),
  );

  const midVector = vector.unit(midAngle);
  const midPoint = point.createOnVector(
    nodeData.position,
    midVector,
    LOOPBACK_EDGE_MID_DISTANCE,
  );

  const controlPoint1 = point.createOnVector(
    nodeData.position,
    startVector,
    LOOPBACK_EDGE_CONTROL_POINT_DISTANCE,
  );
  const controlPoint2 = point.createOnVector(
    nodeData.position,
    endVector,
    LOOPBACK_EDGE_CONTROL_POINT_DISTANCE,
  );
  const { arrowHeads, circle, startCurve, endCurve } = placeEdgeElements(
    edgeDataForPositions,
    startPoint,
    startVector,
    endPoint,
    endVector,
  );

  const line: EdgePositionsLine = [
    startCurve,
    quadraticBezierCurve.createLinePart(controlPoint1, midPoint),
    quadraticBezierCurve.createLinePart(controlPoint2, endCurve),
  ];

  const labels: EdgePositions['labels'] = {
    source: {
      ...ensureLabelWithAnchorFaceRight(startAngle + Math.PI / 18, {
        x: 0,
        y: 0.5,
      }),
      position: point.createOnVector(
        startCurve,
        startVector,
        LOOPBACK_EDGE_TIP_LABEL_DISTANCE,
      ),
    },
    center: {
      ...ensureLabelWithAnchorFaceRight(midAngle + PI_RADIANS_90, {
        x: 0.5,
        y: 0.5,
      }),
      position: point.createOnVector(
        midPoint,
        midVector,
        LOOPBACK_EDGE_MID_LABEL_DISTANCE,
      ),
    },
    target: {
      ...ensureLabelWithAnchorFaceRight(endAngle - Math.PI / 18, {
        x: 0,
        y: 0.5,
      }),
      position: point.createOnVector(
        endCurve,
        endVector,
        LOOPBACK_EDGE_TIP_LABEL_DISTANCE,
      ),
    },
  };

  return {
    arrowHeads,
    circle,
    labels,
    line,
  };
}
